import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { IntrinsicTextNodeStyleProps, View, activeElement, hexColor, Text, Show } from "@lightningtv/solid";
import { Column } from "@lightningtv/solid-ui";
import { useLocation, useNavigate, useParams } from "@solidjs/router";
import seriesProvider from "../../api/providers/series";
import { createInfiniteScroll } from "../../components/pagination";
import seriesCategories from "../../api/providers/seriesCategories";
import { LazyUp } from "@lightningtv/solid/primitives";
import LoadingIndicator from "../loading/loading";
import { focusStore, setFocusStore } from "../../../state";
import { fontFamily, isRTL, t } from "../../translations/configuration";
import ConfirmPin from "../../components/ConfirmPin";
import seriesContinueWatching from "../../api/providers/seriesContinueWatching";
import seriesLastAdded from "../../api/providers/seriesLastAdded";
import seriesFavorites, { hasAnyFavoriteSeries } from "../../api/providers/seriesFavorites";
import Header from "../../components/Header";
import { IconButton } from "../../components/playlistComponents/TopRightHeaders";

const RowStyles: any = {
  display: "flex",
  justifyContent: "flexStart",
  width: 300,
  height: 300,
  gap: 26,
  y: 400,
} satisfies IntrinsicTextNodeStyleProps;

const Series = () => {
  const params = useParams();
  const navigate = useNavigate();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let myButton: any;
  let viewRef: any;
  let search: any;
  let firstRef: any;
  let settings: any;
  let focusTimeout: any;
  let cancelExit;
  let okExit;
  let headerRef: any;
  const location = useLocation();
  const [lastElement, setLastElement] = createSignal<any>(null);
  const [isNumberChanging, setIsNumberChanging] = createSignal(false);
  const [shouldWait, setShouldWait] = createSignal(false);
  const [heldCategory, setHeldCategory] = createSignal<{ id: number; callback: () => void } | null>(null);
  const [focusedCategoryName, setFocusedCategoryName] = createSignal<string>("");

  const [toggledCategoryIDs, setToggledCategoryIDs] = createSignal(
    JSON.parse(localStorage.getItem("seriesCategoryIDs") || "[]")
  );
  const [showPinDialog, setShowPinDialog] = createSignal(false);
  const [currentAction, setCurrentAction] = createSignal(null);
  const [currentCategoryData, setCurrentCategoryData] = createSignal(null);
  const [pendingNavigation, setPendingNavigation] = createSignal(null);
  const [lastFocusedLazyUp, setLastFocusedLazyUp] = createSignal('first');
  const savedPin = localStorage.getItem('userPin');

  const [hasEverAdjustedForCW, setHasEverAdjustedForCW] = createSignal(
    localStorage.getItem("series_cw_adjusted") === "true"
  );

  const providerCategory = createMemo(() => {
    return createInfiniteScroll(seriesCategories(params.filter || "all"));
  });

  const hasContinueWatchingCategory = () => {
    const categories = providerCategory().pages();
    return categories.length > 0 && categories[0]?.categoryID === "series_continue_watching";
  };

  const getAdjustedSelectedIndex = () => {
    const storedIndex = focusStore.seriesIndex;
    if (storedIndex === null || storedIndex === undefined) return 0;

    const currentlyHasCW = hasContinueWatchingCategory();
    const adjustedFlag = localStorage.getItem("series_cw_adjusted");
    if (currentlyHasCW && adjustedFlag !== "true" && storedIndex >= 0) {
      localStorage.setItem("series_cw_adjusted", "true");
      const adjusted = storedIndex + 1;
      return isNaN(adjusted) ? 0 : adjusted; // ADD: guard NaN
    }
    return isNaN(storedIndex) ? 0 : storedIndex; // ADD: guard NaN
  };

  createEffect(() => {
    const categories = providerCategory().pages();
    if (!categories || categories.length === 0) return;

    const savedPin = localStorage.getItem('userPin');
    if (!savedPin) {
      localStorage.removeItem("seriesCategoryIDs");
      return;
    }

    const adultCategoryIDs = categories
      .filter(cat => ["xxx", "porn", "adult"].some(keyword => cat.category_name.toLowerCase().includes(keyword)))
      .map(cat => cat.categoryID);

    const existingIDs = JSON.parse(localStorage.getItem("seriesCategoryIDs") || "[]");
    const mergedIDs = [...new Set([...existingIDs, ...adultCategoryIDs])];
    localStorage.setItem("seriesCategoryIDs", JSON.stringify(mergedIDs));

    const currentlyHasCW = hasContinueWatchingCategory();
    const adjustedFlag = localStorage.getItem("series_cw_adjusted");
    if (!currentlyHasCW && adjustedFlag === "true") {
      localStorage.removeItem("series_cw_adjusted");
    }
  });

  const [number, setNumber] = createSignal<any>(null);
  const [showLoading, setShowLoading] = createSignal(true);
  const [showMessage, setShowMessage] = createSignal(false);

  const handleNumberChange = (newNumber: any) => {
    if (number() === newNumber) return;
    if (timeoutId) clearTimeout(timeoutId);
    setIsNumberChanging(true);
    timeoutId = setTimeout(() => {
      setNumber(newNumber);
      setIsNumberChanging(false);
      timeoutId = null;
    }, 500);
  };

  const provider = createMemo(() => {
    const selectedCategory = number();

    if (selectedCategory === "series_continue_watching") {
      return createInfiniteScroll(seriesContinueWatching(params.filter || "all"));
    }
    if (selectedCategory === "99999" || selectedCategory === "series_favorites") {
      return createInfiniteScroll(seriesFavorites(params.filter || "all"));
    }
    if (selectedCategory === "series_last_added") {
      const scroll = createInfiniteScroll(seriesLastAdded(params.filter || "all"));
      createEffect(() => {
        const pages = scroll.pages();
        if (pages && pages.length > 0) console.log("📊 First item:", pages[0]);
      });
      return scroll;
    }
    return createInfiniteScroll(seriesProvider(params.filter || "all", selectedCategory));
  });

  createEffect(() => {
    localStorage.setItem("@emptyCategory", providerCategory().pages().length > 0 ? "false" : "true");
    const timeout = setTimeout(() => {
      setShowMessage(providerCategory().pages().length > 0 ? false : true);
      setShowLoading(false);
    }, 15000);
    onCleanup(() => clearTimeout(timeout));
  });

  const handlePinConfirm = (pin) => {
    const action = currentAction();
    const categoryData = currentCategoryData();
    const navigation = pendingNavigation();
    if (!categoryData) return;
    const savedPin = localStorage.getItem("userPin");
    if (pin === savedPin) {
      switch (action) {
        case 'lock':
          lockCategory(categoryData.id);
          break;
        case 'unlock':
          if (isAdultCategory(categoryData.id, categoryData.name)) break;
          unlockCategory(categoryData.id);
          break;
        case 'access':
          if (navigation) {
            navigate(navigation.url);
            setFocusStore("moviesDetail", navigation.index);
          }
          break;
      }
    }
    setShowPinDialog(false);
    setCurrentAction(null);
    setCurrentCategoryData(null);
    setPendingNavigation(null);
  };

  const isAdultCategory = (categoryID: string, categoryName: string) => {
    const hasAdultKeyword = ["xxx", "porn", "adult"].some(keyword => categoryName.toLowerCase().includes(keyword));
    const categories = providerCategory().pages();
    const adultCategoryIDs = categories
      .filter(cat => ["xxx", "porn", "adult"].some(keyword => cat.category_name.toLowerCase().includes(keyword)))
      .map(cat => cat.categoryID);
    return hasAdultKeyword || adultCategoryIDs.includes(categoryID);
  };

  const lockCategory = (categoryID) => {
    const savedIDs = [...toggledCategoryIDs()];
    if (!savedIDs.includes(categoryID)) {
      savedIDs.push(categoryID);
      localStorage.setItem("seriesCategoryIDs", JSON.stringify(savedIDs));
      setToggledCategoryIDs(savedIDs);
    }
  };

  const unlockCategory = (categoryID) => {
    const savedIDs = [...toggledCategoryIDs()];
    const indexInArray = savedIDs.indexOf(categoryID);
    if (indexInArray !== -1) {
      savedIDs.splice(indexInArray, 1);
      localStorage.setItem("seriesCategoryIDs", JSON.stringify(savedIDs));
      setToggledCategoryIDs(savedIDs);
    }
  };

  const handleMovieAccess = (movieData, movieIndex, categoryId) => {
    const isLocked = toggledCategoryIDs().includes(categoryId);
    if (isLocked) {
      setCurrentCategoryData({ id: categoryId });
      setCurrentAction('access');
      setPendingNavigation({
        url: `/serieDetails/series/${movieData.series_id}/${movieData.category_id}`,
        index: movieIndex
      });
      setShowPinDialog(true);
    } else {
      navigate(`/serieDetails/series/${movieData.series_id}/${movieData.category_id}`);
      setFocusStore("moviesDetail", movieIndex);
    }
  };

  const [isEnterProcessing, setIsEnterProcessing] = createSignal(false);
  const [isDialogOpenExit, setIsDialogOpenExit] = createSignal<boolean>(false);

  let enterCallCount = 0;
  let enterCallTimer = null;

  const handleRepeatedEnter = (categoryID, toggleCategoryCallback) => {
    enterCallCount++;
    if (enterCallTimer) clearTimeout(enterCallTimer);
    if (enterCallCount === 1) console.log('Started counting Enter presses...');
    enterCallTimer = setTimeout(() => {
      enterCallCount = 0;
    }, 100);
    if (enterCallCount >= 10 && !isEnterProcessing()) {
      setIsEnterProcessing(true);
      enterCallCount = 0;
      if (enterCallTimer) clearTimeout(enterCallTimer);
      if (localStorage.getItem('userPin') === null) {
        setTimeout(() => { cancelExit?.setFocus() }, 600);
        setIsDialogOpenExit(true);
      } else {
        toggleCategoryCallback();
      }
      setTimeout(() => { setIsEnterProcessing(false); }, 1000);
    }
  };

  const enterHoldFunc = (categoryID, toggleCategoryCallback) => {
    if (localStorage.getItem('userPin') === null) {
      setTimeout(() => { cancelExit?.setFocus() }, 600);
      setIsDialogOpenExit(true);
    } else {
      toggleCategoryCallback();
    }
  };

  let holdTimer = -1;
  let enterHeld = false;

  const handleKeyDown = (e) => {
    const held = heldCategory();
    if (!held) return;
    if ((e.key === "Enter" || e.keyCode === 13) && !holdTimer) {
      holdTimer = setTimeout(() => {
        enterHeld = true;
        enterHoldFunc(held.id, held.callback);
      }, 600);
    }
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      if (holdTimer) { clearTimeout(holdTimer); holdTimer = -1; }
      enterHeld = false;
    }
  };

  onMount(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
  });

  onCleanup(() => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
  });

  // ── THUMBNAIL (category left panel) ──────────────────────────────────
  function Thumbnail(props: any) {
    const newData = props;
    const currentCategoryID = props.categoryID;
    const isToggled = () => toggledCategoryIDs().includes(currentCategoryID);
    const isContinueWatchingCategory = currentCategoryID === "series_continue_watching";
    const isLastAddedCategory = currentCategoryID === "series_last_added";
    const isFavoritesCategory = currentCategoryID === "99999";

    const toggleCategory = () => {
      if (isContinueWatchingCategory || isLastAddedCategory || isFavoritesCategory) return;
      const isCurrentlyLocked = isToggled();
      if (isCurrentlyLocked && isAdultCategory(currentCategoryID, newData.category_name)) return;
      setCurrentCategoryData({ id: currentCategoryID, name: newData.category_name });
      setCurrentAction(isCurrentlyLocked ? 'unlock' : 'lock');
      setShowPinDialog(true);
    };

    return (
      <View
        ref={myButton}
        style={{
          width: 480,
          height: 95,
          borderRadius: 12,
          color: hexColor("#15171b"),
          border: { width: 0, color: hexColor("#2563eb") },
          focus: { border: { width: 2, color: hexColor("#2563eb") } },
          transition: { color: { duration: 200 } },
          justifyContent: "center",
          alignItems: "center",
        }}
        onFocus={() => {
          handleNumberChange(newData.categoryID);
          setFocusStore("seriesIndex", newData.index);
          setFocusStore("seriesID", newData.categoryID);
          setFocusedCategoryName(newData.category_name);

          if (!isContinueWatchingCategory && !isLastAddedCategory && !isFavoritesCategory) {
            setHeldCategory({
              id: newData.categoryID,
              callback: () => {
                const isCurrentlyLocked = isToggled();
                setCurrentCategoryData({ id: newData.categoryID, name: newData.category_name });
                setCurrentAction(isCurrentlyLocked ? "unlock" : "lock");
                setShowPinDialog(true);
              },
            });
          } else {
            setHeldCategory(null);
          }
        }}
        onUp={() => { if (shouldWait()) return true; else setFocusStore("seriesDetail", 0); }}
        onDown={() => { if (shouldWait()) return true; else setFocusStore("seriesDetail", 0); }}
      >
        {/* Accent bar — mirrors in RTL */}
        <View
          x={isRTL() ? 476 : 0} y={10}
          width={4} height={75}
          borderRadius={4}
          style={{
            color: hexColor("#00000000"),
            focus: { color: hexColor("#2563eb") },
          }}
        />

        {/* Heart icon for Favorites category — mirrors in RTL */}
        <Show when={isFavoritesCategory}>
          <View
            x={isRTL() ? 434 : 24} y={30}
            width={22} height={22}
            src={hasAnyFavoriteSeries()
              ? "https://i.ibb.co/d4B1MqR/heart-filled.png"
              : "https://i.ibb.co/d4B1MqR/heart-filled.png"}
            style={hasAnyFavoriteSeries() ? {} : { alpha: 0.4 }}
          />
        </Show>

        {/* Category name */}
        <Text
          x={24} y={34}
          width={432}
          contain="width"
          textAlign={isRTL() ? "right" : "left"}
          style={{
            fontSize: 26,
            color: hexColor(isFavoritesCategory && !hasAnyFavoriteSeries() ? "#6b7280" : "#e2e8f0"),
            fontFamily: fontFamily(),
          }}
        >
          {newData.category_name?.length > 30
            ? newData.category_name.substring(0, 30) + "..."
            : newData.category_name}
        </Text>

        {/* Lock / unlock icon — mirrors in RTL */}
        <Show when={savedPin && !isContinueWatchingCategory && !isLastAddedCategory && !isFavoritesCategory}>
          <View
            src={isToggled() ? "https://i.ibb.co/jPvhs2HJ/lock.png" : "https://i.ibb.co/N2Ym8McH/unlock.png"}
            width={24} height={24}
            x={isRTL() ? 26 : 430} y={36}
            style={isToggled() && isAdultCategory(currentCategoryID, newData.category_name) ? { opacity: 0.6 } : {}}
          />
        </Show>
      </View>
    );
  }

  // ── ITEM SERIES (right panel card) ────────────────────────────────────
  function ItemMovie(props: any) {
    const newData = props;
    const rawRating = parseFloat(newData.data.rating_5based);
    const rating = isNaN(rawRating) ? 0 : rawRating;
    const hasContinueWatching = newData?.data?.continue_watching_time > 0;

    const continueWatchingProgress = () => {
      if (!hasContinueWatching) return 0;
      const time = newData.data.continue_watching_time;
      const duration = newData.data.continue_watching_duration;
      if (!duration || duration === 0) return 0;
      return Math.min(Math.max(time / duration, 0), 1);
    };

    const roundedRating = () => Math.round(rating * 2) / 2;

    const handleEnter = () => {
      const currentCategoryId = number();
      if (currentCategoryId === "series_continue_watching" && hasContinueWatching) {
        const season = newData.data.continue_watching_season || "";
        const episode = newData.data.continue_watching_episode || "";
        const seekTime = newData.data.continue_watching_time || 0;
        const seasonNum = season.replace(/\D/g, '');
        const episodeNum = episode.replace(/\D/g, '');
        navigate(`/serieDetails/series/${newData.data.series_id}/${newData.data.category_id}?resumeSeason=${seasonNum}&resumeEpisode=${episodeNum}&seek=${seekTime}`);
      } else {
        handleMovieAccess(newData.data, newData.index, currentCategoryId);
      }
    };

    // Format added date if available
    const addedDate = newData?.data?.releaseDate
      ? (() => {
        try {
          const d = new Date(newData.data.releaseDate); // "2024-09-18" parses natively
          return `Added ${d.getDate()} ${d.toLocaleString('en', { month: 'short' })} ${d.getFullYear()}`;
        } catch { return null; }
      })()
      : null;

    const displayName = focusedCategoryName().length > 30
      ? focusedCategoryName().substring(0, 30) + "..."
      : focusedCategoryName();

    const badgeWidth = Math.min(displayName.length * 9 + 20, 380);

    return (
      <View
        x={20}
        ref={myButton}
        style={{
          borderRadius: 12,
          color: hexColor("#15171b"),
          border: { width: 0, color: hexColor("#2563eb") },
          focus: { border: { width: 2, color: hexColor("#2563eb") } },
          transition: { color: { duration: 200 } },
          height: 200,
          width: 1200,
        }}
        onFocus={() => { localStorage.removeItem("seriesDetail"); }}
        onEnter={handleEnter}
      >
        {/* Accent bar — mirrors in RTL */}
        <View
          x={isRTL() ? 1191 : 5} y={20}
          width={4} height={160}
          borderRadius={4}
          style={{
            color: hexColor("#00000000"),
            focus: { color: hexColor("#2563eb") },
          }}
        />

        {/* Poster thumbnail — right side in RTL */}
        <View
          src={newData.src || newData.data.cover}
          x={isRTL() ? 1052 : 18} y={15}
          width={130} height={170} borderRadius={8}
        />

        {/* Content area */}
        <View x={isRTL() ? 20 : 168} y={20}>

          {/* Title */}
          <Text
            x={0} y={0}
            style={{
              fontSize: 28,
              color: hexColor("#f0f6fc"),
              fontWeight: isRTL() ? "normal" : "bold",
              fontFamily: fontFamily(),
            }}
          >
            {newData.data.name}
          </Text>

          {/* Focused category name badge + date */}
          <View x={0} y={46}>
            <Show when={focusedCategoryName()}>
              <View
                x={0} y={0}
                width={badgeWidth} height={32}
                borderRadius={6}
                color={hexColor("#1d4ed8")}
              >
                <Text x={10} y={7} style={{ fontSize: 18, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold" }}>
                  {focusedCategoryName().length > 30
                    ? focusedCategoryName().substring(0, 30) + "..."
                    : focusedCategoryName()}
                </Text>
              </View>
            </Show>

            <Show when={addedDate}>
              <Text
                x={badgeWidth + 10} y={6}
                style={{ fontSize: 20, color: hexColor("#8b949e") }}
              >
                {addedDate?.toString()}
              </Text>
            </Show>
          </View>

          {/* Stars + IMDb */}
          <View x={0} y={100}>
            {Array.from({ length: 5 }).map((_, index) => {
              const fullStars = Math.floor(rating);
              const hasHalfStar = rating % 1 >= 0.5;
              let starSrc = "https://i.ibb.co/1KBsPWh/star-2.png";
              if (index < fullStars) starSrc = "https://i.ibb.co/4gK6MnK/star-1.png";
              else if (index === fullStars && hasHalfStar) starSrc = "https://i.ibb.co/9V7LVwc/rating.png";
              return <View key={index} x={index * 34} y={0} width={26} height={26} src={starSrc} />;
            })}
            <Text
              x={180} y={3}
              style={{ fontSize: 22, color: hexColor("#8b949e") }}
            >
              {roundedRating().toString()}  IMDb
            </Text>
          </View>

          {/* Continue watching episode title */}
          <Show when={hasContinueWatching}>
            <Text
              x={0} y={135}
              style={{ fontSize: 20, color: hexColor("#f5c518") }}
            >
              {newData.data.continue_watching_episode_title || "Continue watching"}
            </Text>
          </Show>

          {/* Continue watching progress bar */}
          <Show when={hasContinueWatching}>
            <View x={0} y={158} width={400} height={6} borderRadius={3} color={hexColor("#2d3748")}>
              <View
                x={0} y={0}
                width={400 * continueWatchingProgress()}
                height={6}
                borderRadius={3}
                color={hexColor("#f5c518")}
              />
            </View>
          </Show>
        </View>

        {/* Season count badge — top-right (LTR) / top-left (RTL) */}
        <Show when={newData?.data?.num_seasons}>
          <View
            x={isRTL() ? 10 : 1110} y={12}
            width={80} height={32}
            borderRadius={6}
            color={hexColor("#1d4ed8")}
          >
            <Text x={8} y={7} style={{ fontSize: 17, color: hexColor("#ffffff"), fontWeight: isRTL() ? "normal" : "bold" }}>
              S: {newData.data.num_seasons}
            </Text>
          </View>
        </Show>
      </View>
    );
  }

  createEffect(() => {
    const enterID = focusStore.seriesID;
    if (enterID !== null) handleNumberChange(enterID);
  });

  onMount(() => {
    if (focusStore.seriesIndex === null || focusStore.seriesIndex === undefined) {
      //@ts-ignore
      setFocusStore("seriesIndex", 0);
      return;
    }
    setShouldWait(true);
    if (providerCategory() && location.pathname.startsWith("/series")) {
      focusTimeout = setTimeout(() => {
        viewRef?.setFocus();
        setShouldWait(false);
      }, 1500);
    }
  });

  onCleanup(() => { clearTimeout(focusTimeout); });

  return (
    <Show when={showPinDialog()} fallback={
      <View
        color={hexColor("#0d0f14")}
        onEscape={() => {
          if (!shouldWait()) {
            navigate("/home");
            setFocusStore("seriesIndex", null);
            setFocusStore("seriesDetail", null);
            setFocusStore("seriesID", null);
            clearTimeout(focusTimeout);
          }
        }}
      >
        <Header screen={t("bigSeries")} ref={(el) => headerRef = el}
          onRight={() => isRTL() ? null : search?.setFocus()}
          onLeft={() => isRTL() ? settings?.setFocus() : search?.setFocus()}
          onDown={() => {
            if (location.pathname.startsWith("/series")) {
              if (lastFocusedLazyUp() === 'second') viewRef.setFocus();
              else firstRef.setFocus();
            }
          }} />

        {/* ── No-PIN dialog ── */}
        <Show when={isDialogOpenExit()}>
          <View x={500} y={350} width={800} height={300} color={hexColor("#ffffff")} borderRadius={20}>
            <Text x={50} y={30} style={{ fontSize: 24, color: hexColor("#000000"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("pinNotSet")}
            </Text>
            <Text x={50} y={100} style={{ fontSize: 24, color: hexColor("#000000"), fontFamily: fontFamily() }}>
              {t("setPinNow")}
            </Text>
            <View
              ref={el => { cancelExit = el; }}
              x={430} y={220}
              style={{ borderRadius: 15, color: hexColor("#C1C3C1"), focus: { color: hexColor("#128DD3") }, transition: { scale: { duration: 250, easing: "ease-in-out" } }, height: 45, width: 150 }}
              onEnter={() => { firstRef?.setFocus(); setIsDialogOpenExit(false); }}
              textColor={hexColor("#000000")}
              onRight={() => okExit?.setFocus()}
            >
              <Text x={40} y={15} style={{ fontSize: 18, color: hexColor("#000000"), fontFamily: fontFamily() }}>{t("cancelBig")}</Text>
            </View>
            <View
              ref={el => { okExit = el; }}
              x={600} y={220}
              style={{ borderRadius: 15, color: hexColor("#C1C3C1"), focus: { color: hexColor("#128DD3") }, transition: { scale: { duration: 250, easing: "ease-in-out" } }, height: 45, width: 150 }}
              onLeft={() => cancelExit?.setFocus()}
              onEnter={() => navigate("/userinfo?fromPIN=true")}
              textColor={hexColor("#000000")}
            >
              <Text x={60} y={15} style={{ fontSize: 18, color: hexColor("#000000"), fontFamily: fontFamily() }}>{t("ok")}</Text>
            </View>
          </View>
        </Show>

        {/* ── Top-right buttons ── */}
        <View x={isRTL() ? 50 : 1700} y={50}>
          <IconButton
            onRef={(el) => { search = el; }}
            x={0} y={0}
            src="https://i.ibb.co/nNhBcw8M/search.png"
            onRight={() => settings?.setFocus()}
            onDown={() => {
              if (location.pathname.startsWith("/series")) {
                if (lastFocusedLazyUp() === 'second') viewRef?.setFocus();
                else firstRef?.setFocus();
              }
            }}
            onLeft={() => isRTL() ? null : headerRef?.setFocus()}
            onEnter={() => {
              navigate("/searchseries");
              localStorage.removeItem("focusedIndex");
            }}
          />
          <IconButton
            onRef={(el) => { settings = el; }}
            x={100} y={0}
            src="https://i.ibb.co/Jjp8bmn4/setting.png"
            onLeft={() => search?.setFocus()}
            onRight={() => isRTL() ? headerRef?.setFocus() : search?.setFocus()}
            onDown={() => {
              if (location.pathname.startsWith("/series")) {
                if (lastFocusedLazyUp() === 'second') viewRef?.setFocus();
                else firstRef?.setFocus();
              }
            }}
            onEnter={() => navigate("/userInfo")}
          />
        </View>

        {/* ── Main content ── */}
        <View y={150}>
          <Show
            when={providerCategory().pages() && providerCategory().pages().length > 0}
            fallback={
              <View x={750} y={500}>
                <Text fontSize={30} color={hexColor("#7d7e82")} style={{ fontFamily: fontFamily() }}>
                  {t("noSeries")}
                </Text>
              </View>
            }
          >
            <View clipping y={30}>

              {/* ── LEFT: Categories column ── */}
              {/* @ts-ignore */}
              <LazyUp
                onRight={() => {
                  if (!isRTL()) {
                    if (provider().pages() && provider().pages()?.length > 0 && !isNumberChanging()) {
                      viewRef?.setFocus();
                      setShowLoading(false);
                    } else {
                      setShowLoading(true);
                    }
                  }
                }}
                onLeft={() => {
                  if (isRTL()) {
                    if (provider().pages() && provider().pages()?.length > 0 && !isNumberChanging()) {
                      viewRef?.setFocus();
                      setShowLoading(false);
                    } else {
                      setShowLoading(true);
                    }
                  }
                }}
                onBlur={() => setHeldCategory(null)}
                onFocus={() => setLastFocusedLazyUp('first')}
                onUp={() => isRTL() ? headerRef?.setFocus() : search?.setFocus()}
                x={isRTL() ? 1350 : 60}
                y={10}
                gap={10}
                style={RowStyles}
                onSelectedChanged={setLastElement}
                autofocus={true}
                component={Column}
                ref={firstRef}
                scroll="always"
                upCount={15}
                selected={getAdjustedSelectedIndex()}
                each={[{ category_name: "Favorites", categoryID: "99999" }, ...providerCategory().pages()]}
              >
                {(item: any, i: number) => <Thumbnail {...item} index={i} />}
              </LazyUp>

              {/* ── RIGHT: Series column ── */}
              <View x={isRTL() ? 60 : 540} y={10} clipping>
                <Show
                  when={provider().pages() && provider().pages().length > 0 && !isNumberChanging()}
                  fallback={
                    <View x={400} y={300} width={100} height={100}>
                      <Show when={number() !== "series_favorites" && number() !== "99999"}>
                        {showLoading() && <LoadingIndicator isCategoriesIndicator={true} />}
                        {showMessage() && (
                          <Text y={50} style={{ color: hexColor("#7d7e82"), fontSize: 22, width: 10, fontFamily: fontFamily() }}>
                            {t("categoryEmpty")}
                          </Text>
                        )}
                      </Show>
                      <Show when={number() === "series_favorites" || number() === "99999"}>
                        <Text y={50} style={{ color: hexColor("#7d7e82"), fontSize: 22, width: 10, fontFamily: fontFamily() }}>
                          {t("noFavSeries")}
                        </Text>
                      </Show>
                    </View>
                  }
                >
                  {/* @ts-ignore */}
                  <LazyUp
                    x={20}
                    onLeft={() => isRTL() ? null : firstRef?.setFocus()}
                    onRight={() => isRTL() ? firstRef?.setFocus() : null}
                    component={Column}
                    ref={viewRef}
                    onFocus={() => setLastFocusedLazyUp('second')}
                    onUp={() => search?.setFocus()}
                    scroll="always"
                    upCount={6}
                    gap={12}
                    selected={
                      focusStore.seriesDetail === null
                        ? 0
                        : (() => {
                          const index = focusStore.seriesDetail;
                          if (index !== null) {
                            setTimeout(() => { }, 300);
                            return index;
                          }
                        })()
                    }
                    each={provider().pages()}
                  >
                    {(item: any, i: number) => <ItemMovie {...item} index={i} />}
                  </LazyUp>
                </Show>
              </View>

            </View>
          </Show>
        </View>

      </View>}>

      {/* ── PIN dialog overlay ── */}
      <View onEscape={() => {
        if (showPinDialog()) setShowPinDialog(false);
      }}>
        <ConfirmPin onPinSet={handlePinConfirm} />
      </View>
    </Show>
  );
};

export default Series;